
import Vue from 'vue';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { Component, Emit, Inject, Prop } from 'vue-property-decorator';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import Toasted from 'vue-toasted';
import Datepicker from 'vuejs-datepicker';
import { de } from 'vuejs-datepicker/dist/locale';
import { blobDownload, formatDate, WindowType } from '../../../util';
import { TestEquipmentApiUiV2Client, TestEquipmentDevice, TestEquipmentTest } from '../../backend/backendapi';
import { FileStatusMessage, StatusMessage } from '../../dropzone/dropzone.util';
import { RiskOption } from '../archivemodal/archivemodal.model';
import TestEquipmentTestEditModal from '../test/TestEquipmentTestEditModal';
import { paginationOptions, searchOptions } from '../../../util/vue-good-table';
import State from '../State';
import TestEquipmentMeasurementSpecifications from './TestEquipmentMeasurementSpecifications';
import DropZone from '../../dropzone/DropZone';
import { Like } from '../../../util/Like';
import { handleError, initClient } from '../../../api';

declare var window: WindowType;

Vue.use(Toasted, { duration: 2000 });

@Component({
  components: {
    Datepicker,
    DropZone,
    vSelect,
    VueGoodTable,
    ImageViewer: require('../../imageviewer/imageviewer.vue.html').default,
    TestEquipmentMeasurementSpecifications,
    TestEquipmentTestEditModal,
  },
})
export default class TestEquipmentEdit extends Vue {
  @Prop({ required: true })
  readonly equipment!: TestEquipmentDevice;
  @Prop({ required: true })
  readonly tests!: TestEquipmentTest[] | null;
  @Prop({ default: true })
  readonly editable!: boolean;
  @Prop({ default: true })
  readonly showArchiveFields!: boolean;

  @Inject({ from: 'state' })
  readonly state!: State;

  private readonly api = initClient(TestEquipmentApiUiV2Client);

  //config
  datepickerDE: any = de;
  selectConfig = {};

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  @Emit('save')
  save(): void {

  }

  async exportTest(test: Like<TestEquipmentTest>): Promise<void> {
    try {
      const file = await this.api.exportTestToExcel(test.testEquipmentId, test.id);
      blobDownload(file.data, file.fileName ?? 'report.xlsx');
    } catch (err) {
      handleError(this.$toasted, this.$i18n, err);
    }
  }

  fileUploaded(status: FileStatusMessage): void {
    if (status.type !== StatusMessage.success) {
      return;
    }
    this.$emit('file-added', { fileId: status.fileId, equipment: this.equipment });
  }

  removeMedia(mediaId: string): void {
    console.log('rem', mediaId);
    let index = this.equipment.files.findIndex(e => e.id === mediaId);
    if (index > -1) {
      this.equipment.files.splice(index, 1);
    }
  }

  genCSV(): string {
    var table = this.$refs.table as VueGoodTable;

    var cols = this.testColumns.filter(e => e.field !== 'actions');
    var csv = '\ufeff' //utf8 bom (byte order marker)
        + cols.map(e => e.label).join(';') + '\n'
        + ((table.paginated?.length ?? 0) === 0 ? '' : table.paginated[0].children.map(row => {
          return cols.map(col => {
            if (typeof col.field === 'string') {
              return ('' + (row[col.field] ?? '')).split(';').join(',');
            } else if (typeof col.field === 'function') {
              return ('' + (col.field(row) ?? '')).split(';').join(',');
            } else {
              return 'cannot extract value';
            }
          }).join(';');
        }).join('\n'));

    console.log(csv);
    return csv;
  }

  exportCSV(): void {
    const data = this.genCSV();
    const fileName = 'export_tests.csv';

    var a = document.createElement('a') as any;
    document.body.appendChild(a);
    a.style = 'display: none';
    var blob = new Blob([data], { type: 'octet/stream' });
    var url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  get testColumns(): any[] {
    return [
      {
        label: this.$t('testEquipment.edit.protocolNumber').toString(),
        field: 'protocolNumber',
      },
      {
        label: this.$t('testEquipment.edit.testDate').toString(),
        field: (row: TestEquipmentTest) => formatDate(row.testDate),
      },
      {
        label: this.$t('testEquipment.edit.testPerson').toString(),
        field: 'testPerson',
      },
      {
        label: this.$t('testEquipment.edit.testSpecification').toString(),
        field: 'testSpecification',
      },
      {
        label: this.$t('testEquipment.edit.error').toString(),
        field: 'error',
      },
      {
        label: this.$t('testEquipment.edit.grade').toString(),
        field: 'grade',
      },
      {
        label: this.$t('testEquipment.edit.note').toString(),
        field: 'note',
      },
      {
        label: this.$t('general.actions').toString(),
        field: 'actions',
      },
    ];
  }

  get allowMediaDelete(): boolean {
    return window.application.permissions.testequipment.mediadelete;
  }

  get archiveRisk(): string {
    let localRiskOptions = this.state.riskOptionsSetting[this.$i18n.locale] as RiskOption[];
    localRiskOptions ??= this.state.riskOptionsSetting['en'] as RiskOption[];
    if (!localRiskOptions) {
      return this.equipment.archiveRisk!;
    }
    return localRiskOptions.find(e => e.id === this.equipment.archiveRisk!)?.name ?? this.equipment.archiveRisk!;
  }

  onClick(arg: { row: TestEquipmentTest, pageIndex: number, selected: boolean, event: MouseEvent }): void {
    (this.$refs.testModal as TestEquipmentTestEditModal).open(this.equipment, TestEquipmentTest.fromJS(arg.row));
  }
}
